import type { ComponentType, SVGProps } from "react"
import { useState } from "react"
import { useSearch } from "@tanstack/react-router"
import {
	IconCheckboxCheckedOutline18 as CheckboxIcon,
	IconListCheckboxOutline18 as DropdownIcon,
	IconEnvelopeOutline18 as EnvelopeIcon,
	IconFormOutline18 as FormIcon,
	IconGripDotsVerticalOutline18 as GripIcon,
	IconLockOutline18 as LockIcon,
	IconTextInputOutline18 as LongTextIcon,
	IconHashtagOutline18 as NumberIcon,
	IconPlusOutline18 as PlusIcon,
	IconInputFieldOutline18 as ShortTextIcon,
	IconTrashOutline18 as TrashIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import {
	Badge,
	Button,
	IconButton,
	Input,
	Select,
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	Switch,
} from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "@/screens/settings/settings-switches/settings-container"

type FieldType = "shortText" | "longText" | "dropdown" | "checkbox" | "number"

interface Field {
	id: string
	label: string
	type: FieldType | "email" | "message"
	required: boolean
	builtIn?: boolean
	hint?: string
	options?: string[]
}

const TYPE_META: Record<
	Field["type"],
	{ label: string; Icon: ComponentType<SVGProps<SVGSVGElement>> }
> = {
	email: { label: "Email", Icon: EnvelopeIcon },
	message: { label: "Long text", Icon: LongTextIcon },
	shortText: { label: "Short text", Icon: ShortTextIcon },
	longText: { label: "Long text", Icon: LongTextIcon },
	dropdown: { label: "Dropdown", Icon: DropdownIcon },
	checkbox: { label: "Checkbox", Icon: CheckboxIcon },
	number: { label: "Number", Icon: NumberIcon },
}

const TYPE_OPTIONS = (
	["shortText", "longText", "dropdown", "checkbox", "number"] as const
).map((value) => ({ value, label: TYPE_META[value].label }))

const INITIAL_FIELDS: Field[] = [
	{
		id: "email",
		label: "Email",
		type: "email",
		required: true,
		builtIn: true,
		hint: "Only for visitors who are not signed in",
	},
	{
		id: "message",
		label: "Message",
		type: "message",
		required: true,
		builtIn: true,
	},
	{ id: "company", label: "Company name", type: "shortText", required: true },
	{
		id: "area",
		label: "Which part of the product?",
		type: "dropdown",
		required: true,
		options: ["Dashboard", "API", "Billing", "Something else"],
	},
	{
		id: "urgent",
		label: "This blocks my work",
		type: "checkbox",
		required: false,
	},
]

function FieldRow({
	field,
	isOpen,
	onToggle,
	onChange,
	onDelete,
}: {
	field: Field
	isOpen: boolean
	onToggle: () => void
	onChange: (field: Field) => void
	onDelete: () => void
}) {
	const { Icon, label: typeLabel } = TYPE_META[field.type]
	return (
		<div
			className={cn(
				"rounded-lg bg-white ring-1 ring-gray-200 dark:bg-gray-850 dark:ring-gray-700/80",
				isOpen && "ring-gray-300 dark:ring-gray-600",
			)}
		>
			<div className="flex items-center gap-2 py-2 pl-1.5 pr-2">
				<span
					className={cn(
						"flex h-6 w-4 flex-none items-center justify-center text-tertiary [&_svg]:h-3.5 [&_svg]:w-3.5",
						field.builtIn && "invisible",
					)}
				>
					<GripIcon className="flex-none" />
				</span>
				<span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-gray-100 text-secondary dark:bg-gray-800 [&_svg]:h-4 [&_svg]:w-4">
					<Icon className="flex-none" />
				</span>
				<button
					type="button"
					disabled={field.builtIn}
					onClick={onToggle}
					className="flex min-w-0 flex-1 flex-col items-start text-left disabled:opacity-100"
				>
					<span className="w-full truncate text-sm font-medium text-primary">
						{field.label}
					</span>
					<span className="w-full truncate text-xs text-tertiary">
						{field.hint ?? typeLabel}
					</span>
				</button>
				{field.required && <Badge size="sm">Required</Badge>}
				{field.builtIn ? (
					<span className="flex h-7 w-7 flex-none items-center justify-center text-tertiary [&_svg]:h-3.5 [&_svg]:w-3.5">
						<LockIcon className="flex-none" />
					</span>
				) : (
					<IconButton
						Icon={TrashIcon}
						variant="ghost"
						aria-label="Delete field"
						onClick={onDelete}
					/>
				)}
			</div>
			{isOpen && (
				<div className="space-y-4 border-t border-gray-200 px-4 py-4 dark:border-gray-700/80">
					<Input
						name={`${field.id}-label`}
						label="Label"
						value={field.label}
						onChange={(e) => onChange({ ...field, label: e.target.value })}
					/>
					<Select
						name={`${field.id}-type`}
						label="Type"
						value={field.type}
						options={TYPE_OPTIONS}
						onChange={(type) => onChange({ ...field, type: type as FieldType })}
					/>
					{field.type === "dropdown" && (
						<div className="space-y-1.5">
							<div className="text-sm font-medium text-primary">Options</div>
							{(field.options ?? []).map((option, index) => (
								<div key={index} className="flex items-start gap-1.5">
									<Input
										name={`${field.id}-option-${index}`}
										className="flex-1"
										value={option}
										onChange={(e) => {
											const options = [...(field.options ?? [])]
											options[index] = e.target.value
											onChange({ ...field, options })
										}}
									/>
									<IconButton
										Icon={XIcon}
										variant="ghost"
										aria-label="Remove option"
										onClick={() =>
											onChange({
												...field,
												options: field.options?.filter((_, i) => i !== index),
											})
										}
									/>
								</div>
							))}
							<Button
								variant="ghost"
								size="sm"
								Icon={PlusIcon}
								onClick={() =>
									onChange({
										...field,
										options: [...(field.options ?? []), ""],
									})
								}
							>
								Add option
							</Button>
						</div>
					)}
					<Switch
						name={`${field.id}-required`}
						label="Required"
						description="Customers can only send the form once it is filled."
						direction="text-first"
						checked={field.required}
						onCheckedChange={(required) => onChange({ ...field, required })}
					/>
				</div>
			)}
		</div>
	)
}

function ContactFormFieldsSheet({
	open,
	onOpenChange,
	fields,
	setFields,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	fields: Field[]
	setFields: (fields: Field[]) => void
}) {
	const [openId, setOpenId] = useState<string | null>("area")
	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="right"
				className="flex w-full flex-col gap-0 border-gray-200 p-0 sm:max-w-md dark:border-gray-700/80"
			>
				<SheetHeader className="border-b border-gray-200 px-5 py-4 dark:border-gray-700/80">
					<SheetTitle className="text-base">Contact form fields</SheetTitle>
					<SheetDescription>
						Ask for the details your team needs on every request.
					</SheetDescription>
				</SheetHeader>
				<div className="flex-1 space-y-2 overflow-y-auto px-5 pb-8 pt-4">
					{fields.map((field) => (
						<FieldRow
							key={field.id}
							field={field}
							isOpen={openId === field.id}
							onToggle={() => setOpenId(openId === field.id ? null : field.id)}
							onChange={(next) =>
								setFields(fields.map((f) => (f.id === next.id ? next : f)))
							}
							onDelete={() =>
								setFields(fields.filter((f) => f.id !== field.id))
							}
						/>
					))}
					<Button
						variant="outline"
						Icon={PlusIcon}
						className="w-full"
						onClick={() => {
							const id = `field-${fields.length}`
							setFields([
								...fields,
								{ id, label: "New field", type: "shortText", required: false },
							])
							setOpenId(id)
						}}
					>
						Add field
					</Button>
				</div>
			</SheetContent>
		</Sheet>
	)
}

function ContactOptionsCard({
	customCount,
	onCustomize,
}: {
	customCount: number
	onCustomize: () => void
}) {
	const [mode, setMode] = useState("Contact form")
	return (
		<SettingsCardInsetContext.Provider value={false}>
			<SettingsContainer className="space-y-0 divide-y">
				<div className="flex items-center justify-between pb-3">
					<div>
						<div className="text-sm font-medium text-primary">
							Contact options
						</div>
						<div className="mt-1 text-xs text-secondary">
							Choose how customers can reach you.
						</div>
					</div>
					<div className="inline-flex flex-shrink-0 rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
						{["None", "Contact form", "Live Chat"].map((label) => (
							<button
								type="button"
								key={label}
								onClick={() => setMode(label)}
								className={cn(
									"rounded-[0.45rem] px-3 py-1.5 text-xs font-medium",
									label === mode
										? "bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
										: "text-secondary",
								)}
							>
								{label}
							</button>
						))}
					</div>
				</div>
				{mode === "Contact form" && (
					<div className="flex items-center justify-between gap-4 py-3">
						<div className="min-w-0">
							<div className="text-sm font-medium text-primary">
								Form fields
							</div>
							<div className="mt-1 text-xs text-secondary">
								Email, message, and {customCount} custom{" "}
								{customCount === 1 ? "field" : "fields"}.
							</div>
						</div>
						<Button variant="outline" Icon={FormIcon} onClick={onCustomize}>
							Customize fields
						</Button>
					</div>
				)}
				<div className="flex items-center justify-between pt-3">
					<div>
						<div className="text-sm font-medium text-primary">
							Feature requests
						</div>
						<div className="mt-1 text-xs text-secondary">
							Add a link &apos;Request a feature&apos; that opens existing
							requests.
						</div>
					</div>
				</div>
			</SettingsContainer>
		</SettingsCardInsetContext.Provider>
	)
}

export function ContactFormFieldsPage() {
	const drawer = useSearch({ strict: false, select: (s) => s.drawer })
	const [open, setOpen] = useState(drawer === "1")
	const [fields, setFields] = useState(INITIAL_FIELDS)
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[640px] px-6 py-10 pb-24">
				<div className="mb-6">
					<div className="text-lg font-semibold text-primary">
						Widget settings, Customize
					</div>
					<div className="mt-1 text-sm text-secondary">
						Contact form selected. The button opens the fields drawer.
					</div>
				</div>
				<ContactOptionsCard
					customCount={fields.filter((f) => !f.builtIn).length}
					onCustomize={() => setOpen(true)}
				/>
			</div>
			<ContactFormFieldsSheet
				open={open}
				onOpenChange={setOpen}
				fields={fields}
				setFields={setFields}
			/>
		</div>
	)
}
